










































































































import { AppointmentApi } from '@/api/appointments';
import { appointmentProductTypeDescription, appointmentTypeDescription, canEditAppointmentOutcome, instructionTypeDescription, outcomePaymentStatusTypeDescription, outcomeTypeDescription, productPaymentStatusTypeDescription } from '@/components/appointments/appointment-utilities';
import bookAppointmentDialog from '@/components/appointments/book-appointment-dialog.vue';
import completeAppointmentDialog from '@/components/appointments/complete-appointment/complete-appointment-dialog.vue';
import editAppointmentOutcomeDialog from '@/components/appointments/edit-appointment-outcome-dialog.vue';
import editAppointmentOutcomeLegacyDialog from '@/components/appointments/edit-appointment-outcome-legacy-dialog.vue';
import viewAppointmentDialog from '@/components/appointments/view-appointment-dialog.vue';
import { Appointment } from '@/shared/appointments/appointment';
import { AppointmentOutcomeProduct } from '@/shared/appointments/appointment-outcome-product';
import { AppointmentProductPaymentDetails } from '@/shared/appointments/appointment-product-payment-details';
import { ClientAccount } from '@/shared/clients/client-account';
import { InstructionType } from '@/shared/instruction-type';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { ClientStatusType } from '@/shared/types/client-status-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const typesModule = namespace('types');
const userModule = namespace('user');
const usersModule = namespace('users');
const appointmentApi = new AppointmentApi();

@Component({
    components: {
        bookAppointmentDialog,
        completeAppointmentDialog,
        editAppointmentOutcomeDialog,
        editAppointmentOutcomeLegacyDialog,
        viewAppointmentDialog,
    },
})
export default class ClientAppointments extends Vue {
    @typesModule.Getter('activeClientStatusTypes') private statusTypes!: Array<ClientStatusType>;
    @typesModule.Getter('appointmentTypes') private appointmentTypes!: Array<AppointmentType>;
    @typesModule.Getter('appointmentOutcomeTypes') private outcomeTypes!: Array<AppointmentOutcomeType>;
    @typesModule.Getter('activeAppointmentPaymentStatusTypes') private appointmentPaymentStatusTypes!: Array<AppointmentPaymentStatusType>;

    @userModule.Getter('user') private currentUser!: User | null;
    @userModule.Getter('isAdministrator') private isAdministrator!: boolean;
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isCustomerExperienceExecutive') private isCustomerExperienceExecutive!: boolean;
    @userModule.Getter('isCustomerExperienceExecutiveTeamLeader') private isCustomerExperienceExecutiveTeamLeader!: boolean;
    @userModule.Getter('isHeadOfDepartment') private isHeadOfDepartment!: boolean;
    @userModule.Getter('isLegalAdvisor') private isLegalAdvisor!: boolean;
    @userModule.Getter('isLegalAssistant') private isLegalAssistant!: boolean;
    @userModule.Getter('isDataCreationExecutive') private isDataCreationExecutive!: boolean;
    @userModule.Getter('isProbateLegalAdvisor') private isProbateLegalAdvisor!: boolean;

    @usersModule.Getter('users') public users!: Array<User>;

    @Prop({ required: true }) public client!: ClientAccount;
    @Prop({ required: true }) public appointments!: Array<Appointment>;

    private saving: boolean = false;
    private selectedAppointment: Appointment | null = null;
    private viewAppointmentDialogVisible: boolean = false;
    private completeAppointmentDialogVisible: boolean = false;
    private editOutcomeDialogVisible: boolean = false;
    private editOutcomeLegacyDialogVisible: boolean = false;
    private bookAppointmentDialogVisible: boolean = false;
    private deleteAppointmentConfirmationDialogVisible: boolean = false;
    private deleteOutcomeConfirmationDialogVisible: boolean = false;
    private isAppointmentRebook: boolean = false;
    private isAppointmentEdit: boolean = false;
    private showProductTabs: boolean = false;

    private get orderedAppointments(): Array<Appointment> {
        return this.appointments.sort((n1, n2) => {
            if (n1.date! < n2.date!) {
                return 1;
            }

            if (n1.date! > n2.date!) {
                return -1;
            }

            return 0;
        });
    }

    private isLegalAdvisorAt(partnerPracticeId: string | null): boolean {
        return this.isLegalAdvisor && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private isLegalAssistantAt(partnerPracticeId: string | null): boolean {
        return this.isLegalAssistant && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private isHeadOfDepartmentAt(partnerPracticeId: string | null): boolean {
        return this.isHeadOfDepartment && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private canCreateOutcome(appointment: Appointment): boolean {
        const partnerPracticeId = this.client.partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isAdministrator ||
                this.isInternalKeyStakeholder ||
                (this.isLegalAdvisorAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                (this.isLegalAssistantAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId)
            );

        return validUser && appointment.id != null && appointment.outcome == null && !appointment.isRebooked;
    }

    private canEditOutcome(appointment: Appointment): boolean {
        const partnerPracticeId = this.client.partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isAdministrator ||
                this.isInternalKeyStakeholder ||
                (this.isLegalAdvisorAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                (this.isLegalAssistantAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId)
            );

        return validUser && canEditAppointmentOutcome(appointment);
    }

    private canEditAppointment(appointment: Appointment): boolean {
        return !appointment.isRebooked;
    }

    private canRebookAppointment(appointment: Appointment): boolean {
        const partnerPracticeId = this.client.partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isAdministrator ||
                this.isInternalKeyStakeholder ||
                this.isCustomerExperienceExecutive ||
                this.isCustomerExperienceExecutiveTeamLeader ||
                this.isLegalAdvisorAt(partnerPracticeId) ||
                this.isLegalAssistantAt(partnerPracticeId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId) ||
                this.isDataCreationExecutive ||
                this.isProbateLegalAdvisor
            );

        const accountStatus = this.statusTypes.find((type) => type.id === this.client.accountStatusId);

        if (appointment.outcome == null) {
            return validUser && appointment.id != null && !appointment.isRebooked && !this.client.doNotContact && !accountStatus!.isDeceased && !accountStatus!.isAccountArchived;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === appointment.outcome!.outcomeTypeId);

        if (outcomeType == null) {
            return false;
        }

        return validUser && appointment.id != null && !appointment.isRebooked && !outcomeType.isSat && !this.client.doNotContact && !accountStatus!.isDeceased && !accountStatus!.isAccountArchived;
    }

    private canDeleteAppointment(): boolean {
        return this.isAdministrator;
    }

    private canDeleteOutcome(appointment: Appointment): boolean {
        return this.isAdministrator && appointment.outcome != null;
    }

    private showViewAppointmentDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.viewAppointmentDialogVisible = true;
    }

    private showCompleteAppointmentDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.completeAppointmentDialogVisible = true;
    }

    private showEditOutcomeDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;

        if (appointment.outcome == null) {
            return;
        }

        if (appointment.outcome.isLegacy) {
            this.editOutcomeLegacyDialogVisible = true;
            return;
        }

        this.showProductTabs = appointment.outcome.products != null && appointment.outcome.products.length > 0;
        this.editOutcomeDialogVisible = true;
    }

    private showEditAppointmentDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.isAppointmentEdit = true;
        this.bookAppointmentDialogVisible = true;
    }

    private showRebookAppointmentDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.isAppointmentRebook = true;
        this.bookAppointmentDialogVisible = true;
    }

    private showDeleteAppointmentConfirmationDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.deleteAppointmentConfirmationDialogVisible = true;
    }

    private showDeleteOutcomeConfirmationDialog(appointment: Appointment): void {
        this.selectedAppointment = appointment;
        this.deleteOutcomeConfirmationDialogVisible = true;
    }

    private async onDeleteAppointment(): Promise<void> {
        this.saving = true;
        try {
            await appointmentApi.deleteAppointmentAsync(this.selectedAppointment!);
            this.$emit('appointment-removed', this.selectedAppointment);
        } catch (e) {
            this.$snackbar.open({ message: 'Could not delete appointment, please try again.', type: 'is-danger' });
        }
        this.saving = false;
        this.onCancel();
    }

    private async onDeleteOutcome(): Promise<void> {
        this.saving = true;
        try {
            await appointmentApi.deleteAppointmentOutcomeAsync(this.selectedAppointment!);
            this.$emit('outcome-removed', this.selectedAppointment);
        } catch (e) {
            this.$snackbar.open({ message: 'Could not delete appointment outcome, please try again.', type: 'is-danger' });
        }
        this.saving = false;
        this.onCancel();
    }

    private onCancel(): void {
        this.viewAppointmentDialogVisible = false;
        this.completeAppointmentDialogVisible = false;
        this.editOutcomeDialogVisible = false;
        this.editOutcomeLegacyDialogVisible = false;
        this.bookAppointmentDialogVisible = false;
        this.deleteAppointmentConfirmationDialogVisible = false;
        this.deleteOutcomeConfirmationDialogVisible = false;
        this.isAppointmentRebook = false;
        this.isAppointmentEdit = false;
    }

    private isUnsaved(appointment: Appointment): boolean {
        return (appointment.id == null) || (appointment.outcome != null && appointment.outcome.id == null);
    }

    private isRebook(appointment: Appointment): boolean {
        return appointment.originalAppointmentId != null;
    }

    private isRebooked(appointment: Appointment): boolean {
        return appointment.isRebooked!;
    }

    private isPending(appointment: Appointment): boolean {
        return appointment.outcome == null && !appointment.isRebooked;
    }

    private showOutcomeType(appointment: Appointment): boolean {
        return appointment.outcome != null;
    }

    private showInstructionType(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        const outcome = appointment.outcome;

        const appointmentType = this.appointmentTypes.find((type) => type.id === appointment.appointmentTypeId);

        if (appointmentType == null || !appointmentType.isInitialAppointment) {
            return false;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === outcome.outcomeTypeId);

        return outcomeType != null && outcomeType.isSat;
    }

    private showNotInstructed(appointment: Appointment): boolean {
        if (appointment.outcome == null || appointment.outcome.isLegacy) {
            return false;
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === appointment.appointmentTypeId);

        if (appointmentType == null || !appointmentType.isInitialAppointment) {
            return false;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === appointment.outcome!.outcomeTypeId);

        if (outcomeType == null || !outcomeType.isSat) {
            return false;
        }

        return appointment.outcome.products == null || appointment.outcome.products.every((product) => product.instructionType === InstructionType.NotInstructed);
    }

    private showOutcomePaymentStatus(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        return appointment.outcome.isLegacy && appointment.outcome.instructionType === InstructionType.Instructed;
    }

    private showOutcomeProducts(appointment: Appointment): boolean {
        if (appointment.outcome == null) {
            return false;
        }

        return !appointment.outcome.isLegacy && appointment.outcome.products.length > 0;
    }

    private showProductPaymentStatus(product: AppointmentOutcomeProduct): boolean {
        if (product == null) {
            return false;
        }

        return product.instructionType === InstructionType.Instructed;
    }

    private outcomeTypeDescription(outcomeTypeId: string | null): string {
        return outcomeTypeDescription(outcomeTypeId);
    }

    private instructionTypeDescription(instructionType: InstructionType): string {
        return instructionTypeDescription(instructionType);
    }

    private appointmentProductTypeDescription(productTypeId: string): string {
        return appointmentProductTypeDescription(productTypeId);
    }

    private outcomePaymentStatusTypeDescription(paymentStatusId: string | null) {
        return outcomePaymentStatusTypeDescription(paymentStatusId);
    }

    private productPaymentStatusTypeDescription(paymentDetails: Array<AppointmentProductPaymentDetails> | null): string {
        return productPaymentStatusTypeDescription(paymentDetails);
    }

    private appointmentTypeDescription(appointmentTypeId: string | null): string {
        return appointmentTypeDescription(appointmentTypeId);
    }

    private userName(userId: string): string {
        const user = this.users.find((u) => u.id === userId);
        return user == null ? '' : `${user.firstName.trim()} ${user.lastName.trim()}`.trim();
    }

    private onAppointmentBooked(): void {
        if (this.isAppointmentEdit) {
            this.$emit('appointment-updated');
        } else {
            this.$emit('appointment-rebooked');
        }

        this.onCancel();
    }

    private onBookingCancel(appointment: Appointment): void {
        this.onCancel();
    }

    private onCompleted(): void {
        this.$emit('appointment-completed');
        this.onCancel();
    }

    private onCompletionCancel(): void {
        this.selectedAppointment = null;
        this.onCancel();
    }

    private onOutcomeUpdated(): void {
        this.$emit('appointment-outcome-changed');
        this.onCancel();
    }

    private onViewCancel(): void {
        this.onCancel();
    }

    private async mounted(): Promise<void> {
        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`types/loadPartnerPracticeTypesAsync`),
            this.$store.dispatch(`users/loadAllUsersAsync`),
        ]);
    }
}
